import { NgModule, enableProdMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GoogleMapsModule } from '@angular/google-maps';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SearchComponent } from './components/search/search.component';
import { LayoutComponent } from './components/layout/layout.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { LandingScreenComponent } from './components/landing-screen/landing-screen.component';

import { PropertyDetailsService } from './services/property-details.service';
import { LoginComponent } from './components/login/login.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { SignInComponent } from './components/sign-in/sign-in.component';
import { HomeDetailsComponent } from './components/home-details/home-details.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { LoaderComponent } from './components/loader/loader.component';
import { ParkingComponent } from './components/parking/parking.component';
import { EstimateBoxComponent } from './components/estimate-box/estimate-box.component';
import { BasementComponent } from './components/basement/basement.component';
import { InteriorComponent } from './components/interior/interior.component';
import { ExteriorComponent } from './components/exterior/exterior.component';
import { ListingDetailsComponent } from './components/listing-details/listing-details.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MyListingComponent } from './components/my-listing/my-listing.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { PhotosComponent } from './components/photos/photos.component';
import { PlansAddonComponent } from './components/plans-addon/plans-addon.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AddonComponent } from './components/addon/addon.component';
import { MatDialogModule } from '@angular/material/dialog';
import { GreenButtonComponent } from './components/ui-components/buttons/green-button/green-button.component';
import { MatRadioModule } from '@angular/material/radio';
import { SwitchGreenComponent } from './components/ui-components/switch-green/switch-green.component';
import { ReviewComponent } from './components/review/review.component';
import { CheckboxComponent } from './components/ui-components/checkbox/checkbox.component';
import { MainMenuComponent } from './components/main-menu/main-menu.component';
import { LightGreenButtonComponent } from './components/ui-components/buttons/light-green-button/light-green-button.component';
import { EmailComponent } from './components/email/email.component';
import { DeleteDialogComponent } from './components/ui-components/dialog/delete-dialog/delete-dialog.component';
import { ValidateEmailComponent } from './components/validate-email/validate-email.component';
import { OutlinedGreenButtonComponent } from './components/ui-components/buttons/outlined-green-button/outlined-green-button.component';
import { GreenButtonV2Component } from './components/ui-components/buttons/green-button-v2/green-button-v2.component';
import { IconButtonComponent } from './components/ui-components/buttons/icon-button/icon-button.component';
import { PhoneFormattorDirective } from './phone-formattor.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CookieService } from 'ngx-cookie-service';
import { AppService } from './app.service';
import { CurrencyFormatterDirective } from './currency-formatter.directive';
import { DecimalInputDirective } from './decimal-input.directive';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { EstimateTabBoxComponent } from './components/estimate-tab-box/estimate-tab-box.component';
import { StepperSaveComponent } from './components/stepper-save/stepper-save.component';
import {
  PktrAppMenuModule,
  PktrAppMenuService,
} from '@prospektr/pktr-app-menu';
import { DynamicFormComponent } from './shared/components/dynamic-form/dynamic-form.component';
import { MatButtonModule } from '@angular/material/button';
import { HttpInterceptorService } from './services/http-interceptor.service';

enableProdMode();

@NgModule({
  declarations: [
    AppComponent,
    SearchComponent,
    LayoutComponent,
    FooterComponent,
    HeaderComponent,
    LandingScreenComponent,
    LoginComponent,
    SignInComponent,
    HomeDetailsComponent,
    StepperComponent,
    LoaderComponent,
    ParkingComponent,
    EstimateBoxComponent,
    BasementComponent,
    InteriorComponent,
    ExteriorComponent,
    ListingDetailsComponent,
    MyListingComponent,
    ResetPasswordComponent,
    PhotosComponent,
    PlansAddonComponent,
    AddonComponent,
    GreenButtonComponent,
    SwitchGreenComponent,
    ReviewComponent,
    CheckboxComponent,
    MainMenuComponent,
    LightGreenButtonComponent,
    EmailComponent,
    DeleteDialogComponent,
    ValidateEmailComponent,
    OutlinedGreenButtonComponent,
    GreenButtonV2Component,
    IconButtonComponent,
    PhoneFormattorDirective,
    CurrencyFormatterDirective,
    DecimalInputDirective,
    EstimateTabBoxComponent,
    StepperSaveComponent,
    DynamicFormComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatInputModule,
    MatSliderModule,
    MatFormFieldModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    GoogleMapsModule,
    NgxSliderModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatTabsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatRadioModule,
    MatTooltipModule,
    DragDropModule,
    PktrAppMenuModule,
  ],
  providers: [
    AppService,
    CookieService,
    PropertyDetailsService,
    PktrAppMenuService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private appService: AppService) {}
}
